import { BottomSheet } from '@/components/BottomSheet';
import { ChevronDownIcon, MinusIcon, PlusIcon } from '@/components/icons';
import { useIsMobile } from '@/hooks/useMediaQuery';
import type { SkierCounts } from '@/store/book-ski-pass.store';
import { classNames } from '@/utils/classNames';
import {
  buildSkierTypesFromRestrictions,
  type DomainRestriction,
} from '@/utils/domain-restrictions';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './SkierTypesDropdown.module.css';

type SkierTypesDropdownProps = {
  value: SkierCounts;
  onChange: (counts: SkierCounts) => void;
  restrictions?: DomainRestriction[] | null;
  hasSelectedDomain?: boolean;
};

export const SkierTypesDropdown = ({
  value,
  onChange,
  restrictions,
  hasSelectedDomain = false,
}: SkierTypesDropdownProps) => {
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState(false);
  const [localCounts, setLocalCounts] = useState<SkierCounts>(value);
  const containerRef = useRef<HTMLDivElement>(null);

  const skierTypes = useMemo(
    () => buildSkierTypesFromRestrictions(restrictions ?? undefined),
    [restrictions]
  );
  const hasRestrictionInfo = skierTypes.length > 0;
  const showMissingRestrictionsInfo = hasSelectedDomain && !hasRestrictionInfo;

  const totalSkiers = Object.values(value).reduce((sum, count) => sum + count, 0);

  useEffect(() => {
    setLocalCounts(value);
  }, [value]);

  useEffect(() => {
    if (isMobile) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
        setLocalCounts(value);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobile, isOpen, value]);

  const handleTriggerClick = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const handleIncrement = useCallback(
    (key: string) => {
      const current = localCounts[key];
      const next = { ...localCounts, [key]: (typeof current === 'number' ? current : 0) + 1 };
      setLocalCounts(next);
      onChange(next);
    },
    [localCounts, onChange]
  );

  const handleDecrement = useCallback(
    (key: string) => {
      const current = localCounts[key];
      const next = {
        ...localCounts,
        [key]: Math.max(0, (typeof current === 'number' ? current : 0) - 1),
      };
      setLocalCounts(next);
      onChange(next);
    },
    [localCounts, onChange]
  );

  const getSummaryText = () => {
    if (showMissingRestrictionsInfo) {
      return 'No age categories available';
    }

    if (totalSkiers === 0) {
      return hasRestrictionInfo ? 'Select skiers' : 'Select a resort first';
    }

    const parts: string[] = [];
    for (const t of skierTypes) {
      const count = value[t.key] ?? 0;
      if (count <= 0) continue;
      const label = count <= 1 ? t.label : t.label.toLowerCase() == 'child' ? 'Children' : `${t.label}s`;
      parts.push(`${String(count)} ${label}`);
    }
    return parts.join(', ');
  };

  const skierListContent = hasRestrictionInfo ? (
    <div className={styles.skierList}>
      {skierTypes.map((skierType) => (
        <div key={skierType.key} className={styles.skierRow}>
          <div className={styles.skierInfo}>
            <span className={styles.skierLabel}>{skierType.label}</span>
            <span className={styles.skierAge}>{skierType.ageRange}</span>
          </div>
          <div className={styles.controls}>
            <button
              type="button"
              className={classNames(
                styles.countButton,
                (localCounts[skierType.key] ?? 0) === 0 && styles.disabled
              )}
              onClick={() => {
                handleDecrement(skierType.key);
              }}
              disabled={(localCounts[skierType.key] ?? 0) === 0}
              aria-label={`Decrease ${skierType.label} count`}
            >
              <MinusIcon className={styles.buttonIcon} />
            </button>
            <span className={styles.count}>{localCounts[skierType.key] ?? 0}</span>
            <button
              type="button"
              className={styles.countButton}
              onClick={() => {
                handleIncrement(skierType.key);
              }}
              aria-label={`Increase ${skierType.label} count`}
            >
              <PlusIcon className={styles.buttonIcon} />
            </button>
          </div>
        </div>
      ))}
    </div>
  ) : (
    showMissingRestrictionsInfo && (
      <p className={styles.missingRestrictionsInfo}>
        We have no information about selected domain age categories
      </p>
    )
  );

  return (
    <div className={styles.container} ref={containerRef}>
      <button
        type="button"
        className={classNames(styles.trigger, isOpen && styles.open)}
        onClick={handleTriggerClick}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span className={classNames(totalSkiers === 0 && styles.placeholder)}>
          {getSummaryText()}
        </span>
        <ChevronDownIcon className={classNames(styles.chevron, isOpen && styles.rotated)} />
      </button>

      {isMobile ? (
        <BottomSheet
          isOpen={isOpen}
          onClose={() => {
            setLocalCounts(value);
            setIsOpen(false);
          }}
          title="Who's skiing?"
          footer={
            hasRestrictionInfo ? (
              <p className={styles.footerHelp}>
                Please provide each participant&apos;s age for the ski-pass period
              </p>
            ) : undefined
          }
          footerClassName={hasRestrictionInfo ? styles.skierSheetFooter : undefined}
        >
          {skierListContent}
        </BottomSheet>
      ) : (
        isOpen && (
          <div className={styles.dropdown}>
            {skierListContent}
            {hasRestrictionInfo && (
              <div className={styles.footer}>
                <p className={styles.footerHelp}>
                  Please provide each participant&apos;s age for the ski-pass period
                </p>
              </div>
            )}
          </div>
        )
      )}
    </div>
  );
};
